import {
  DirectionalLight,
  Mesh,
  HemisphereLightHelper,
  HemisphereLight,
  Vector3,
  SphereGeometry,
  MeshStandardMaterial,
  Object3D,
  Color,
} from "three";
import { CSM, CSMMode } from "three/examples/jsm/csm/CSM.js";

import { getPosition } from "suncalc";

import { geolocations, stadiumInfo } from "../../../../constants/geolocations";
import { PoserClock } from "../../../util/clock";
import { Context } from "../../../context";
import { LightLevelProbe } from "./probe";
import { SkyShader } from "./shader";
import { SkyFallback } from "./fallback";

const MS_IN_HOUR = 1000 * 60 * 60;

const mapSize = 2048 * 2;

export type SkyLightingParams = {
  debugPosition: boolean;
  debugTime: boolean;
  debugTimeOffset: number;
  elevation: number;
  azimuth: number;
  debugSun: boolean;
  sunIntensity: number;
  sunColor: number;
  debugHemi: boolean;
  hemiIntensity: number;
  hemiGroundColor: number;
  hemiSkyColor: number;

  shadow: {
    near: number;
    far: number;
    top: number;
    bottom: number;
    left: number;
    right: number;

    bias: number;
    radius: number;

    mapSize: number;

    // vsm:
    blurSamples: number;
    vsmBias: number;

    // csm
    csmMode: CSMMode;
  };
};

export class SkyLighting extends Object3D {
  params: SkyLightingParams = {
    debugPosition: false,
    debugTime: false,
    debugTimeOffset: 0,
    elevation: 30,
    azimuth: 180,

    debugSun: false,
    sunIntensity: 1,
    sunColor: 0xffffff,
    debugHemi: false,
    hemiIntensity: 1,
    hemiGroundColor: 0x253e25,
    hemiSkyColor: 0x2c56c0,

    shadow: {
      near: 1,
      far: 5000,
      top: 750,
      bottom: -750,
      left: -750,
      right: 750,

      // bias: 0.0005,
      bias: -0.0001,
      radius: 2,

      mapSize: mapSize,

      // vsm:
      blurSamples: 1,
      vsmBias: -0.0001,

      csmMode: "practical",
    },
  };

  sky: SkyShader | SkyFallback;

  private probe?: LightLevelProbe;

  private hemiParams = {
    day: {
      colors: {
        sky: new Color(0x2c56c0),
        ground: new Color(0x253e25),
      },
      intensity: 1.6,
    },
    night: {
      colors: {
        sky: new Color(0xa8b1e1),
        ground: new Color(0x1e2022),
      },
      intensity: 9.5,
    },
  };

  private sunParams = {
    midday: {
      color: new Color(0xe9edc9),
      intensity: 9.5,
    },
    sunset: {
      color: new Color(0xc07d45),
      intensity: 6.3,
    },
  };

  private elevationLimits = {
    min: -40,
    transStart: 30,
    transWidth: 30,
    max: 90,
  };

  private prevSunCoords = {
    phi: 0,
    theta: 0,
  };
  private sunCoordTolerance = (5 * Math.PI) / 180;
  private limitOverride = {
    complete: false,
    inProgress: false,
  };
  private minLightness = 0.175;

  private sunDistance = 750;

  private sunNormal = new Vector3();
  sun: DirectionalLight | CSM;
  private hemi: HemisphereLight;

  private helpers: {
    sun: Mesh;
    hemi: HemisphereLightHelper;
  };

  private venueInfo: stadiumInfo;

  constructor(context: Context, venueId?: number) {
    super();

    const { container, userOptions } = context;

    const debugSphere = new Mesh(
      new SphereGeometry(5, 32, 32),
      new MeshStandardMaterial({ color: 0xffff00 })
    );

    if (userOptions.values.materials.spec != "basic") {
      this.probe = new LightLevelProbe(context);
    }

    const bias = ["vsm", "csm"].includes(userOptions.values.shadows.type)
      ? this.params.shadow.vsmBias
      : this.params.shadow.bias;
    this.sun =
      userOptions.values.shadows.type === "csm" &&
      userOptions.values.shadows.cascades !== undefined
        ? new CSM({
            maxFar: 1000,
            cascades: userOptions.values.shadows.cascades,
            shadowMapSize: this.params.shadow.mapSize,
            lightNear: this.params.shadow.near,
            lightFar: this.params.shadow.far,
            mode: this.params.shadow.csmMode,
            shadowBias: bias,
            lightIntensity: this.params.sunIntensity,
            // NB: not included in constructor yet, so set below
            // lightColor: new Color(this.params.sunColor),
            camera: context.camera,
            parent: this,
          })
        : new DirectionalLight(this.params.sunColor, this.params.sunIntensity);

    if (this.sun instanceof DirectionalLight) {
      if (userOptions.values.shadows) {
        this.sun.castShadow = true;
        this.sun.shadow.camera.near = this.params.shadow.near;
        this.sun.shadow.camera.far = this.params.shadow.far;
        this.sun.shadow.camera.top = this.params.shadow.top;
        this.sun.shadow.camera.bottom = this.params.shadow.bottom;
        this.sun.shadow.camera.left = this.params.shadow.left;
        this.sun.shadow.camera.right = this.params.shadow.right;
        // TODO: decide on and benchmark shadows
        this.sun.shadow.bias = bias;
        this.sun.shadow.radius = this.params.shadow.radius;
        this.sun.shadow.mapSize.width = this.params.shadow.mapSize;
        this.sun.shadow.mapSize.height = this.params.shadow.mapSize;
      }

      this.sun.target.position.set(0, 0, 0);
      this.sun.position.set(0, this.sunDistance, 0);

      this.sun.add(debugSphere);

      this.add(this.sun);
    } else {
      this.sun.fade = true;

      for (const light of this.sun.lights) {
        light.shadow.camera.top = this.params.shadow.top;
        light.shadow.camera.bottom = this.params.shadow.bottom;
        light.shadow.camera.left = this.params.shadow.left;
        light.shadow.camera.right = this.params.shadow.right;

        light.color.setHex(this.params.sunColor);
      }
    }

    const { clouds, dynamic, staticTexture } = userOptions.values.sky;

    if (staticTexture) {
      this.sky = new SkyFallback(context);
    } else {
      this.sky = new SkyShader(
        container?.clientWidth,
        container?.clientHeight,
        clouds,
        dynamic
      );
      this.sky.scale.setScalar(450000);
    }

    this.hemi = new HemisphereLight(
      this.params.hemiSkyColor,
      this.params.hemiGroundColor,
      this.params.hemiIntensity
    );
    this.hemi.position.set(0, 120, 0);

    this.venueInfo =
      geolocations.find((loc) => loc.id === venueId) || geolocations[0];

    this.helpers = {
      sun: debugSphere,
      hemi: new HemisphereLightHelper(this.hemi, 10),
    };

    this.helpers.sun.visible = false;
    this.helpers.hemi.visible = false;

    this.add(this.helpers.hemi);

    if (this.sky) this.add(this.sky);

    this.add(this.hemi);
  }

  init() {
    this.probe?.init();
    this.sky.init();
  }

  teardown() {
    this.probe?.teardown();
    if (this.sun instanceof CSM) {
      this.sun.dispose();
    }
  }

  update(clock: PoserClock) {
    if (!this.params.debugPosition) {
      this.updateSunPosition(clock);
    }

    this.updateLightLevels();

    if (this.params.debugSun) {
      if (this.sun instanceof DirectionalLight) {
        this.sun.intensity = this.params.sunIntensity;
        this.sun.color.setHex(this.params.sunColor);
      } else {
        for (const light of this.sun.lights) {
          light.intensity = this.params.sunIntensity;
          light.color.setHex(this.params.sunColor);
        }
      }
    }

    if (this.params.debugHemi) {
      this.hemi.intensity = this.params.hemiIntensity;
      this.hemi.color.setHex(this.params.hemiSkyColor);
      this.hemi.groundColor.setHex(this.params.hemiGroundColor);
    }

    const phi = (Math.PI / 180) * (90 - this.params.elevation);
    const theta = (Math.PI / 180) * this.params.azimuth;

    this.sunNormal.setFromSphericalCoords(1, phi, theta);

    if (this.sun instanceof DirectionalLight) {
      this.sunNormal.setLength(this.sunDistance);
      this.sun.position.copy(this.sunNormal);
    } else {
      this.sun.lightDirection.copy(this.sunNormal).negate();
    }

    this.probeLightLevels(phi, theta);

    this.sky.update(clock, this.sunNormal);
  }

  helpersOn(on: boolean) {
    this.helpers.sun.visible = on;
    this.helpers.hemi.visible = on;
  }

  private probeLightLevels(phi: number, theta: number) {
    if (
      this.elevationLimits.transStart + this.elevationLimits.transWidth >=
      this.elevationLimits.max
    ) {
      this.limitOverride.complete = true;
    }

    if (this.limitOverride.complete || !this.probe) return;

    const deltaPhi = Math.abs(this.prevSunCoords.phi - phi);
    const deltaTheta = Math.abs(this.prevSunCoords.theta - theta);
    if (
      !this.limitOverride.inProgress &&
      deltaPhi < this.sunCoordTolerance &&
      deltaTheta < this.sunCoordTolerance
    )
      return;

    const lightness = this.probe.sample();
    if (lightness !== undefined && lightness < this.minLightness) {
      this.elevationLimits.transStart += 2.5;
      this.limitOverride.inProgress = true;
    } else if (this.limitOverride.inProgress) {
      console.log(
        `overriding elevation limits ${lightness}: ${this.params.elevation}`
      );

      this.limitOverride.complete = true;
    }

    this.prevSunCoords.phi = phi;
    this.prevSunCoords.theta = theta;
  }

  private updateSunPosition(clock: PoserClock) {
    const debugOffsetHr = this.params.debugTime
      ? this.params.debugTimeOffset
      : undefined;
    const dOffset = debugOffsetHr ? debugOffsetHr * MS_IN_HOUR : 0;

    const sunPos = getPosition(
      // NB: give suncalc number to avoid js `Date` messing up timezones
      //@ts-ignore
      clock.realMs + dOffset,
      this.venueInfo.latitude,
      this.venueInfo.longitude
    );

    this.params.elevation = Math.max(
      sunPos.altitude * (180 / Math.PI),
      this.elevationLimits.min
    );
    this.params.azimuth = sunPos.azimuth * (180 / Math.PI);
  }

  private updateLightLevels() {
    const transEnd =
      this.elevationLimits.transStart - this.elevationLimits.transWidth;

    // update sun
    if (!this.params.debugSun) {
      const alpha = Math.max(
        0,
        (this.params.elevation - transEnd) /
          (this.elevationLimits.max - transEnd)
      );
      const delta =
        this.sunParams.midday.intensity - this.sunParams.sunset.intensity;

      const lights =
        this.sun instanceof DirectionalLight ? [this.sun] : this.sun.lights;
      for (const light of lights) {
        light.intensity = this.sunParams.sunset.intensity + delta * alpha;

        light.color.lerpColors(
          this.sunParams.sunset.color,
          this.sunParams.midday.color,
          alpha
        );

        // update params for debug
        this.params.sunColor = light.color.getHex();
        this.params.sunIntensity = light.intensity;
      }
    }

    // update hemi
    if (!this.params.debugHemi) {
      if (this.params.elevation > this.elevationLimits.transStart) {
        this.hemi.groundColor.copy(this.hemiParams.day.colors.ground);
        this.hemi.color.copy(this.hemiParams.day.colors.sky);
        this.hemi.intensity = this.hemiParams.day.intensity;
      } else if (this.params.elevation > transEnd) {
        const alpha =
          (this.params.elevation - transEnd) /
          (this.elevationLimits.transStart - transEnd);
        this.hemi.groundColor.lerpColors(
          this.hemiParams.night.colors.ground,
          this.hemiParams.day.colors.ground,
          alpha
        );
        this.hemi.color.lerpColors(
          this.hemiParams.night.colors.sky,
          this.hemiParams.day.colors.sky,
          alpha
        );

        const deltaIntensity =
          this.hemiParams.night.intensity - this.hemiParams.day.intensity;
        this.hemi.intensity =
          this.hemiParams.day.intensity + deltaIntensity * (1 - alpha);
      } else {
        this.hemi.groundColor.copy(this.hemiParams.night.colors.ground);
        this.hemi.color.copy(this.hemiParams.night.colors.sky);
        this.hemi.intensity = this.hemiParams.night.intensity;
      }

      // update params for debug
      this.params.hemiGroundColor = this.hemi.groundColor.getHex();
      this.params.hemiSkyColor = this.hemi.color.getHex();
      this.params.hemiIntensity = this.hemi.intensity;
    }
  }
}
